import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';
import { ANIMAL_LABELS } from '../lib/animals';
import {
  Loader2,
  ArrowLeft,
  Phone,
  MapPin,
  PawPrint,
  Users,
  CheckCircle2,
  XCircle,
  CalendarClock,
  CreditCard,
  Repeat,
  StickyNote,
  FileText,
  AlertTriangle,
  Clock,
  Footprints,
  Bone,
  Anchor,
  Hourglass,
  UserPlus,
  DollarSign,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import OfflineSyncBanner, { PendingSyncChip } from '../components/OfflineSyncBanner';
import InstallPwaPrompt from '../components/InstallPwaPrompt';
import VoiceRecorder from '../components/VoiceRecorder';
import RemindersList from '../components/RemindersList';
import { syncVoiceQueue } from '../lib/voiceQueue';
import AddAppointmentDialog from '../components/AddAppointmentDialog';
import InvoiceBuilderDialog from '../components/InvoiceBuilderDialog';
import PageHelp from '../components/help/PageHelp';
import { markFieldViewReviewed } from '../lib/tutorial';
import {
  cacheFieldView,
  loadCachedFieldView,
  queueAction,
  applyOptimisticPatch,
} from '../lib/offlineStore';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAY_BADGE = {
  PAID: { className: 'bg-green-100 text-green-800 border-green-300', label: 'Paid' },
  PARTIAL: { className: 'bg-amber-100 text-amber-800 border-amber-300', label: 'Partial' },
  UNPAID: { className: 'bg-red-100 text-red-700 border-red-300', label: 'Unpaid' },
};

const STATUS_BADGE = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELED: 'bg-gray-100 text-gray-700',
  NO_SHOW: 'bg-red-100 text-red-700',
};

export default function FieldViewPage() {
  const { token, logout, user } = useAuth();  const navigate = useNavigate();
  const [data, setData] = useState({ today: [], tomorrow: [], thisWeek: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('today');
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [reminderReload, setReminderReload] = useState(0);

  // Dialogs
  const [noteJob, setNoteJob] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [rescheduleJob, setRescheduleJob] = useState(null);
  const [newDateTime, setNewDateTime] = useState('');
  const [partialJob, setPartialJob] = useState(null);
  const [partialDue, setPartialDue] = useState('');
  const [partialPaid, setPartialPaid] = useState('');
  const [partialMethod, setPartialMethod] = useState('');

  // Session 27 — one-tap "Got Paid" with optional amount override
  const [gotPaidJob, setGotPaidJob] = useState(null);
  const [gotPaidAmount, setGotPaidAmount] = useState('');
  const [gotPaidMethod, setGotPaidMethod] = useState('Cash');

  useEffect(() => {
    load();
    markFieldViewReviewed(user?.id);
    const flush = () => { if (navigator.onLine && token) syncVoiceQueue(token); };
    flush();
    window.addEventListener('online', flush);
    return () => window.removeEventListener('online', flush);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/dashboard/field-view`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const fresh = res.data || { today: [], tomorrow: [], thisWeek: [], counts: {} };
      setData(fresh);
      // Cache today/tomorrow/thisWeek for offline field use
      cacheFieldView(fresh);
    } catch (e) {
      // Network failed — fall back to local cache if we have one
      const cached = loadCachedFieldView();
      if (cached) {
        setData(cached);
        toast.message('Showing cached jobs (offline mode)');
      } else {
        toast.error('Could not load field view');
      }
    } finally {
      setLoading(false);
    }
  };

  const patchBooking = async (bookingId, payload, successMsg) => {
    // If we're offline, queue the action and update the UI optimistically.
    // The OfflineSyncBanner will flush the queue when the connection returns.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      queueAction('patch_booking', bookingId, payload);
      const next = applyOptimisticPatch(bookingId, payload);
      if (next) setData(next);
      toast.success(`${successMsg} (queued — will sync when online)`);
      return;
    }

    setActionLoadingId(bookingId);
    try {
      await axios.patch(`${API_URL}/api/dashboard/bookings/${bookingId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(successMsg);
      await load();
    } catch (err) {
      // Network blip mid-request — degrade to queued
      const isNetwork = !err.response;
      if (isNetwork) {
        queueAction('patch_booking', bookingId, payload);
        const next = applyOptimisticPatch(bookingId, payload);
        if (next) setData(next);
        toast.success(`${successMsg} (queued — will sync when online)`);
      } else {
        toast.error(err.response?.data?.detail || 'Update failed');
      }
    } finally {
      setActionLoadingId(null);
    }
  };

  const reschedule = async () => {
    if (!rescheduleJob || !newDateTime) return;
    setActionLoadingId(rescheduleJob.id);
    try {
      await axios.post(
        `${API_URL}/api/dashboard/bookings/${rescheduleJob.id}/reschedule`,
        { startDateTime: newDateTime },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Rescheduled');
      setRescheduleJob(null);
      setNewDateTime('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reschedule failed');
    } finally {
      setActionLoadingId(null);
    }
  };

  const saveNote = async () => {
    if (!noteJob) return;
    const newText = noteText.trim();
    if (!newText) {
      setNoteJob(null);
      setNoteText('');
      return;
    }
    // Offline: queue with kind=add_note so the sync engine merges against the
    // latest server-side providerNotes (avoids overwriting concurrent edits)
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      queueAction('add_note', noteJob.id, { note: newText });
      const merged = [(noteJob.providerNotes || '').trim(), newText].filter(Boolean).join('\n');
      const next = applyOptimisticPatch(noteJob.id, { providerNotes: merged });
      if (next) setData(next);
      toast.success('Note added (queued — will sync when online)');
      setNoteJob(null);
      setNoteText('');
      return;
    }
    const merged = [(noteJob.providerNotes || '').trim(), newText].filter(Boolean).join('\n');
    await patchBooking(noteJob.id, { providerNotes: merged }, 'Note added');
    setNoteJob(null);
    setNoteText('');
  };

  const openPartial = (job) => {
    const due = job.amountDueCents != null ? (job.amountDueCents / 100).toFixed(2) : '';
    const paid = job.amountPaidCents != null ? (job.amountPaidCents / 100).toFixed(2) : '';
    setPartialJob(job);
    setPartialDue(due);
    setPartialPaid(paid);
    setPartialMethod(job.paymentMethod || '');
  };

  const savePartial = async () => {
    if (!partialJob) return;
    const dueCents = Math.round((parseFloat(partialDue) || 0) * 100);
    const paidCents = Math.round((parseFloat(partialPaid) || 0) * 100);
    if (dueCents <= 0) {
      toast.error('Enter the total amount due');
      return;
    }
    if (paidCents <= 0) {
      toast.error('Enter how much was paid');
      return;
    }
    if (paidCents >= dueCents) {
      toast.error('Paid is greater than or equal to Due — use "Paid" instead');
      return;
    }
    await patchBooking(
      partialJob.id,
      {
        amountDueCents: dueCents,
        amountPaidCents: paidCents,
        paymentMethod: partialMethod.trim() || null,
      },
      'Partial payment saved'
    );
    setPartialJob(null);
    setPartialDue('');
    setPartialPaid('');
    setPartialMethod('');
  };

  const handlePay = (job, status) => {
    if (status === 'PARTIAL') {
      openPartial(job);
      return;
    }
    // Paid → set amountPaid = amountDue (or current due) so balance = 0
    // Unpaid → reset amountPaid to 0; preserve amountDue if already set
    if (status === 'PAID') {
      const due = job.amountDueCents || 0;
      if (due > 0) {
        // Send paymentStatus too so offline optimistic UI flips the badge
        // immediately (server still recomputes from amounts on sync).
        patchBooking(
          job.id,
          { amountDueCents: due, amountPaidCents: due, paymentStatus: 'PAID' },
          'Marked paid'
        );
        return;
      }
      // No amountDue set — just flip status (legacy compat)
      patchBooking(job.id, { paymentStatus: 'PAID' }, 'Marked paid');
      return;
    }
    if (status === 'UNPAID') {
      patchBooking(
        job.id,
        { amountPaidCents: 0, paymentStatus: 'UNPAID' },
        'Marked unpaid'
      );
      return;
    }
  };

  // Session 27 — open "Got Paid" dialog pre-filled with full amount + Cash default.
  const openGotPaid = (job) => {
    const dueCents = job.amountDueCents
      ?? (job.service?.priceCents || job.priceCents || 0);
    setGotPaidJob(job);
    setGotPaidAmount(dueCents > 0 ? (dueCents / 100).toFixed(2) : '');
    setGotPaidMethod(job.paymentMethod || 'Cash');
  };

  const saveGotPaid = async () => {
    if (!gotPaidJob) return;
    const paidCents = Math.round((parseFloat(gotPaidAmount) || 0) * 100);
    if (paidCents <= 0) {
      toast.error('Enter an amount');
      return;
    }
    const dueCents = gotPaidJob.amountDueCents
      ?? (gotPaidJob.service?.priceCents || gotPaidJob.priceCents || paidCents);
    const status = paidCents >= dueCents ? 'PAID' : 'PARTIAL';
    await patchBooking(
      gotPaidJob.id,
      {
        amountDueCents: dueCents,
        amountPaidCents: paidCents,
        paymentMethod: gotPaidMethod || null,
        paymentStatus: status,
      },
      status === 'PAID' ? 'Payment logged' : 'Partial payment logged'
    );
    setGotPaidJob(null);
    setGotPaidAmount('');
    setGotPaidMethod('Cash');
  };

  const list = tab === 'today' ? data.today : tab === 'tomorrow' ? data.tomorrow : data.thisWeek;

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Compact mobile header */}
      <header className="sticky top-0 z-20 bg-card border-b shadow-sm">
        <div className="max-w-3xl mx-auto px-3 py-3 flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Logo variant="compact" size="sm" />
          <div className="ml-auto">
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/login'); }}>
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-3 py-3">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            <h1 className="text-2xl font-bold" data-testid="field-view-title">
              Field View
            </h1>
            <PageHelp
              testId="field"
              title="Field View (Today)"
              purpose="This is your work list for today, tomorrow, and this week. It works even with no signal."
              actions={[
                { label: 'Got Paid', desc: 'log a payment the moment a client pays you' },
                { label: 'Mark Done', desc: 'check off a finished job' },
                { label: 'Needs Invoice', desc: 'flag a job you still need to bill' },
                { label: 'Create Invoice', desc: 'turn a job into a professional invoice' },
                { label: 'Add Note', desc: 'save a private note — only you can see it' },
              ]}
              nextStep="Tap a client's phone number to call them on your way."
            />
          </div>
          <AddAppointmentDialog onCreated={load} />
        </div>

        {/* Needs-invoice chip (Session 31) — shows total flagged across
            today + tomorrow + this week so providers can clear their
            billing queue from the field. */}
        {(() => {
          const buckets = [data.today, data.tomorrow, data.thisWeek];
          const seen = new Set();
          let count = 0;
          for (const list of buckets) {
            for (const j of (list || [])) {
              if (j.needsInvoice && !seen.has(j.id)) {
                seen.add(j.id);
                count++;
              }
            }
          }
          if (count === 0) return null;
          return (
            <button
              type="button"
              onClick={() => {
                navigate('/dashboard');
                // Defer to next tick so PaymentSummaryCard is mounted before
                // we ask it to open. window event keeps this decoupled.
                setTimeout(() => {
                  window.dispatchEvent(new CustomEvent('rw:open-needs-invoice'));
                }, 50);
              }}
              className="mb-3 w-full sm:w-auto flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 hover:bg-amber-100 transition px-3 py-1.5 text-sm text-amber-900"
              data-testid="needs-invoice-chip"
            >
              <FileText className="h-4 w-4" />
              <span><strong>{count}</strong> job{count === 1 ? '' : 's'} need{count === 1 ? 's' : ''} invoicing</span>
            </button>
          );
        })()}

        {/* Offline sync banner — hidden when online + nothing queued */}
        <div className="mb-3">
          <OfflineSyncBanner onSynced={load} />
        </div>

        {/* PWA Install prompt — appears once per 14 days when the browser
            fires beforeinstallprompt or iOS Safari is detected. */}
        <InstallPwaPrompt />

        {/* Daily reminders (RW-BUILD-005) — only shows when something is due */}
        <div className="mb-3">
          <RemindersList scope="due" reloadSignal={reminderReload} hideWhenEmpty />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-3 h-12 sticky top-[57px] z-10 bg-background">
            <TabsTrigger value="today" data-testid="tab-today" className="text-sm">
              Today
              {data.counts.today > 0 && (
                <span className="ml-1 inline-flex items-center justify-center h-5 min-w-5 rounded-full bg-primary text-primary-foreground text-xs px-1">
                  {data.counts.today}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="tomorrow" data-testid="tab-tomorrow" className="text-sm">
              Tomorrow
              {data.counts.tomorrow > 0 && (
                <span className="ml-1 inline-flex items-center justify-center h-5 min-w-5 rounded-full bg-primary text-primary-foreground text-xs px-1">
                  {data.counts.tomorrow}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="week" data-testid="tab-week" className="text-sm">
              This Week
              {data.counts.thisWeek > 0 && (
                <span className="ml-1 inline-flex items-center justify-center h-5 min-w-5 rounded-full bg-primary text-primary-foreground text-xs px-1">
                  {data.counts.thisWeek}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value={tab} className="space-y-3 mt-4">
            {loading ? (
              <div className="flex justify-center py-8 text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : list.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <CheckCircle2 className="h-10 w-10 mx-auto opacity-40 mb-2" />
                  <p className="font-medium text-foreground">All clear</p>
                  <p className="text-sm">No jobs scheduled.</p>
                </CardContent>
              </Card>
            ) : (
              list.map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  loading={actionLoadingId === job.id}
                  onMarkDone={() =>
                    patchBooking(job.id, { status: 'COMPLETED' }, 'Marked done')
                  }
                  onNoShow={() =>
                    patchBooking(job.id, { status: 'NO_SHOW' }, 'Marked no-show')
                  }
                  onPay={(p) => handlePay(job, p)}
                  onGotPaid={() => openGotPaid(job)}
                  onReschedule={() => {
                    setRescheduleJob(job);
                    setNewDateTime((job.startDateTime || '').slice(0, 16));
                  }}
                  onAddNote={() => {
                    setNoteJob(job);
                    setNoteText('');
                  }}
                  onNeedsInvoice={() =>
                    patchBooking(
                      job.id,
                      { needsInvoice: !job.needsInvoice },
                      job.needsInvoice ? 'Invoice flag cleared' : 'Flagged: Needs invoice'
                    )
                  }
                  onReload={load}
                />
              ))
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Global voice-note recorder for the field */}
      <VoiceRecorder variant="fab" onSaved={() => setReminderReload((r) => r + 1)} />

      {/* Add Note Dialog */}
      <Dialog open={!!noteJob} onOpenChange={(o) => !o && setNoteJob(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
          </DialogHeader>
          {noteJob?.providerNotes && (
            <div className="text-xs bg-muted p-2 rounded">
              <p className="font-medium mb-1">Existing notes</p>
              <p className="whitespace-pre-wrap">{noteJob.providerNotes}</p>
            </div>
          )}
          <Textarea
            rows={4}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Quick note — gets appended to your private notes."
            maxLength={2000}
            autoFocus
            data-testid="field-note-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteJob(null)}>
              Cancel
            </Button>
            <Button onClick={saveNote} disabled={!noteText.trim()} data-testid="field-note-save">
              Save Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={!!rescheduleJob} onOpenChange={(o) => !o && setRescheduleJob(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reschedule</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Currently:{' '}
            {rescheduleJob?.startDateTime &&
              format(parseISO(rescheduleJob.startDateTime), 'EEE MMM d, h:mm a')}
          </p>
          <Input
            type="datetime-local"
            value={newDateTime}
            onChange={(e) => setNewDateTime(e.target.value)}
            data-testid="field-reschedule-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleJob(null)}>
              Cancel
            </Button>
            <Button onClick={reschedule} disabled={!newDateTime} data-testid="field-reschedule-save">
              Move Booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Got Paid Dialog (Session 27) — quick one-tap with optional override */}
      <Dialog open={!!gotPaidJob} onOpenChange={(o) => !o && setGotPaidJob(null)}>
        <DialogContent className="max-w-md" data-testid="got-paid-dialog">
          <DialogHeader>
            <DialogTitle>Got Paid</DialogTitle>
          </DialogHeader>
          {gotPaidJob && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">{gotPaidJob.client?.name || gotPaidJob.clientName || 'Client'}</strong>
                {gotPaidJob.service?.name && <> · {gotPaidJob.service.name}</>}
              </p>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Amount received ($)</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={gotPaidAmount}
                  onChange={(e) => setGotPaidAmount(e.target.value)}
                  className="h-11 text-lg"
                  data-testid="got-paid-amount"
                  autoFocus
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Pre-filled with the full amount. Edit if partial.
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Method</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {['Cash', 'Check', 'Venmo', 'Zelle', 'Card', 'Other'].map((m) => (
                    <Button
                      key={m}
                      type="button"
                      size="sm"
                      variant={gotPaidMethod === m ? 'default' : 'outline'}
                      onClick={() => setGotPaidMethod(m)}
                      data-testid={`got-paid-method-${m.toLowerCase()}`}
                    >
                      {m}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setGotPaidJob(null)}>Cancel</Button>
            <Button
              onClick={saveGotPaid}
              className="bg-green-600 hover:bg-green-700"
              data-testid="got-paid-save"
            >
              <DollarSign className="h-4 w-4 mr-1" /> Mark Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partial Payment Dialog */}
      <Dialog open={!!partialJob} onOpenChange={(o) => !o && setPartialJob(null)}>
        <DialogContent className="max-w-md" data-testid="partial-payment-dialog">
          <DialogHeader>
            <DialogTitle>Partial Payment</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {partialJob?.client?.name && (
              <>
                <strong className="text-foreground">{partialJob.client.name}</strong> ·{' '}
              </>
            )}
            {partialJob?.service?.name}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Total due ($)</label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={partialDue}
                onChange={(e) => setPartialDue(e.target.value)}
                placeholder="0.00"
                autoFocus
                data-testid="partial-due-input"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Paid so far ($)</label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={partialPaid}
                onChange={(e) => setPartialPaid(e.target.value)}
                placeholder="0.00"
                data-testid="partial-paid-input"
              />
            </div>
          </div>
          {partialDue && partialPaid && parseFloat(partialDue) > parseFloat(partialPaid) && (
            <p className="text-sm text-amber-700">
              Balance remaining:{' '}
              <strong>
                {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                  Math.max(0, parseFloat(partialDue) - parseFloat(partialPaid))
                )}
              </strong>
            </p>
          )}
          <div>
            <label className="text-xs text-muted-foreground">Payment method (optional)</label>
            <Input
              value={partialMethod}
              onChange={(e) => setPartialMethod(e.target.value)}
              placeholder="Cash, Check, Venmo, Zelle…"
              maxLength={100}
              data-testid="partial-method-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPartialJob(null)}>
              Cancel
            </Button>
            <Button onClick={savePartial} data-testid="partial-save-btn">
              Save Partial
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function JobCard({ job, loading, onMarkDone, onNoShow, onPay, onGotPaid, onReschedule, onAddNote, onNeedsInvoice, onReload }) {
  const time = job.startDateTime ? format(parseISO(job.startDateTime), 'h:mm a') : '';
  const pb = PAY_BADGE[job.paymentStatus || 'UNPAID'];
  const isDone = job.status === 'COMPLETED' || job.status === 'NO_SHOW';
  const hasAnimalAlert = (job.animals || []).some(
    (a) => a.handlingNotes || a.behaviorNotes || a.medicalNotes
  );

  return (
    <Card data-testid={`job-${job.id}`} className={isDone ? 'opacity-70' : ''}>
      <CardContent className="p-4 space-y-3">
        {/* Top row: time/order + status + payment */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            {job.stopOrder != null ? (
              <div className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">
                #{job.stopOrder}
              </div>
            ) : (
              <div className="flex items-center gap-1 text-base font-semibold">
                <Clock className="h-4 w-4" /> {time}
              </div>
            )}
            {job.stopOrder != null && time && (
              <span className="text-sm text-muted-foreground">{time}</span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={STATUS_BADGE[job.status] || ''}>{job.status}</Badge>
            {job.isRecurring && (
              <Badge variant="outline" className="border-primary/40 text-primary" data-testid="fieldview-recurring-badge">
                <Repeat className="h-3 w-3 mr-1" /> Recurring
              </Badge>
            )}
            <Badge variant="outline" className={pb.className + ' border'}>
              <CreditCard className="h-3 w-3 mr-1" /> {pb.label}
            </Badge>
            {job.needsInvoice && (
              <Badge
                className="bg-amber-100 text-amber-800 border border-amber-300"
                data-testid={`needs-invoice-badge-${job.id}`}
              >
                <FileText className="h-3 w-3 mr-1" /> Invoice
              </Badge>
            )}
            {!job.needsInvoice && job.invoicedAt && (
              <Badge
                className="bg-blue-100 text-blue-800 border border-blue-300"
                data-testid={`invoice-sent-badge-${job.id}`}
              >
                <FileText className="h-3 w-3 mr-1" /> Invoice Sent
              </Badge>
            )}
            <PendingSyncChip bookingId={job.id} />
          </div>
        </div>

        {/* Client + location */}
        <div className="space-y-1">
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <p className="text-lg font-bold leading-tight" data-testid={`job-${job.id}-client`}>
              {job.client?.name || 'Client'}
            </p>
            {job.client?.phone && (
              <a
                href={`tel:${job.client.phone}`}
                className="text-primary inline-flex items-center text-sm font-medium hover:underline"
              >
                <Phone className="h-4 w-4 mr-1" /> {job.client.phone}
              </a>
            )}
          </div>
          <p className="text-sm text-muted-foreground flex items-start gap-1">
            <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>
              {job.barnName ? <strong className="text-foreground">{job.barnName}</strong> : null}
              {job.barnName && (job.jobLocation || job.locationAddress) ? ' — ' : ''}
              {job.jobLocation || job.locationAddress}
            </span>
          </p>
          <p className="text-sm">{job.service?.name}</p>
        </div>

        {/* Per-animal line items (Session 30) — when present, this is the
            authoritative animal+service list for this booking. */}
        {Array.isArray(job.appointmentItems) && job.appointmentItems.length > 0 && (
          <div className="rounded border border-emerald-200 bg-emerald-50 p-2" data-testid={`items-${job.id}`}>
            <p className="text-xs font-semibold text-emerald-900 mb-1">Animals & services</p>
            <ul className="space-y-0.5 text-sm">
              {job.appointmentItems.map((it) => (
                <li key={it.itemId} className="flex items-center justify-between gap-2">
                  <span className="truncate">
                    <strong>{it.animalName || 'Unlabeled'}</strong>
                    {it.animalType ? ` · ${(ANIMAL_LABELS[it.animalType] || it.animalType.toLowerCase())}` : ''}
                    {' — '}{it.serviceName || 'Service'}
                  </span>
                  {typeof it.priceCents === 'number' && (
                    <span className="text-emerald-900 font-medium tabular-nums">${(it.priceCents/100).toFixed(2)}</span>
                  )}
                </li>
              ))}
            </ul>
            {typeof job.totalCents === 'number' && job.totalCents > 0 && (
              <p className="text-right text-xs font-semibold text-emerald-900 mt-1">
                Total: ${(job.totalCents/100).toFixed(2)}
              </p>
            )}
          </div>
        )}

        {/* Animals */}
        {job.animals && job.animals.length > 0 ? (
          <div className="text-sm flex items-start gap-2 bg-muted/40 p-2 rounded">
            <PawPrint className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
            <span>
              {job.animals
                .map(
                  (a) =>
                    `${a.name}${a.animalType ? ` (${ANIMAL_LABELS[a.animalType] || a.animalType})` : ''}`
                )
                .join(', ')}
            </span>
          </div>
        ) : job.animalQuantity && job.animalType ? (
          <div className="text-sm flex items-center gap-2 bg-muted/40 p-2 rounded">
            <Users className="h-4 w-4 text-primary" />
            <span>
              {job.animalQuantity}{' '}
              {(ANIMAL_LABELS[job.animalType] || job.animalType).toLowerCase()}
              {job.animalQuantity > 1 ? 's' : ''}
              {job.groupDescription && (
                <span className="text-muted-foreground"> — {job.groupDescription}</span>
              )}
            </span>
          </div>
        ) : null}

        {/* Warnings — client-shared animal notes (handling/behavior/medical) */}
        {hasAnimalAlert && (
          <div className="text-sm flex items-start gap-2 bg-amber-50 border border-amber-200 p-2 rounded text-amber-900">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span className="line-clamp-2">
              {job.animals
                .map((a) => a.handlingNotes || a.behaviorNotes || a.medicalNotes)
                .filter(Boolean)
                .join(' • ')}
            </span>
          </div>
        )}

        {/* Private provider flags — kicks/bites/stocks/extra-time/helper.
            Only this provider sees these. Compact icon chips. */}
        <PrivateFlagChips animals={job.animals || []} privateNotes={job.privateAnimalNotes} />

        {/* Notes preview */}
        {(job.notes || job.providerNotes) && (
          <div className="text-xs text-muted-foreground space-y-1">
            {job.notes && (
              <p className="flex items-start gap-1">
                <StickyNote className="h-3 w-3 mt-0.5 flex-shrink-0" />
                <span className="line-clamp-2">
                  <strong className="text-foreground">Client:</strong> {job.notes}
                </span>
              </p>
            )}
            {job.providerNotes && (
              <p className="flex items-start gap-1">
                <StickyNote className="h-3 w-3 mt-0.5 flex-shrink-0" />
                <span className="line-clamp-2">
                  <strong className="text-foreground">Yours:</strong> {job.providerNotes}
                </span>
              </p>
            )}
          </div>
        )}

        {/* Got Paid — primary one-tap action with optional amount override (Session 27) */}
        {job.paymentStatus !== 'PAID' && (
          <div className="pt-2 border-t">
            <Button
              size="lg"
              onClick={onGotPaid}
              disabled={loading}
              className="w-full h-11 bg-green-600 hover:bg-green-700 text-white font-semibold"
              data-testid={`got-paid-${job.id}`}
            >
              <DollarSign className="h-5 w-5 mr-1" /> Got Paid
            </Button>
          </div>
        )}

        {/* Quick actions */}
        <div className="pt-2 border-t grid grid-cols-2 gap-2">
          <Button
            size="sm"
            onClick={onMarkDone}
            disabled={loading || job.status === 'COMPLETED'}
            className="h-10"
            data-testid={`mark-done-${job.id}`}
          >
            <CheckCircle2 className="h-4 w-4 mr-1" /> Mark Done
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onNoShow}
            disabled={loading || job.status === 'NO_SHOW'}
            className="h-10 text-red-600"
            data-testid={`no-show-${job.id}`}
          >
            <XCircle className="h-4 w-4 mr-1" /> No Show
          </Button>
          <Button
            size="sm"
            variant={job.paymentStatus === 'PAID' ? 'default' : 'outline'}
            onClick={() => onPay('PAID')}
            disabled={loading}
            className="h-10"
            data-testid={`pay-paid-${job.id}`}
          >
            Paid
          </Button>
          <Button
            size="sm"
            variant={job.paymentStatus === 'PARTIAL' ? 'default' : 'outline'}
            onClick={() => onPay('PARTIAL')}
            disabled={loading}
            className="h-10"
            data-testid={`pay-partial-${job.id}`}
          >
            Partial
          </Button>
          <Button
            size="sm"
            variant={job.paymentStatus === 'UNPAID' || !job.paymentStatus ? 'default' : 'outline'}
            onClick={() => onPay('UNPAID')}
            disabled={loading}
            className="h-10"
            data-testid={`pay-unpaid-${job.id}`}
          >
            Unpaid
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onReschedule}
            disabled={loading}
            className="h-10"
            data-testid={`reschedule-${job.id}`}
          >
            <CalendarClock className="h-4 w-4 mr-1" /> Reschedule
          </Button>
          <Button
            size="sm"
            variant={job.needsInvoice ? 'default' : 'outline'}
            onClick={onNeedsInvoice}
            disabled={loading}
            className={`h-10 col-span-2 ${job.needsInvoice ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}`}
            data-testid={`needs-invoice-${job.id}`}
            title={job.needsInvoice ? 'Click to clear the invoice flag' : 'Flag this job so you remember to send an invoice'}
          >
            <FileText className="h-4 w-4 mr-1" />
            {job.needsInvoice ? '✓ Needs Invoice (tap to clear)' : 'Needs Invoice'}
          </Button>
          <InvoiceBuilderDialog
            booking={job}
            trigger={
              <Button
                size="sm"
                variant="secondary"
                disabled={loading}
                className="h-10 col-span-2"
                data-testid={`create-invoice-${job.id}`}
              >
                <FileText className="h-4 w-4 mr-1" /> Create Invoice
              </Button>
            }
            onSent={() => onReload && onReload()}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={onAddNote}
            disabled={loading}
            className="h-10 col-span-2"
            data-testid={`add-note-${job.id}`}
          >
            <StickyNote className="h-4 w-4 mr-1" /> Add Note
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PrivateFlagChips({ animals, privateNotes }) {
  if (!privateNotes || Object.keys(privateNotes).length === 0) return null;

  const FLAG_DEFS = [
    { key: 'kicksOnBackFeet', label: 'kicks', Icon: Footprints },
    { key: 'bites', label: 'bites', Icon: Bone },
    { key: 'needsStocks', label: 'stocks', Icon: Anchor },
    { key: 'requiresExtraTime', label: 'extra time', Icon: Hourglass },
    { key: 'needsHelper', label: 'helper', Icon: UserPlus },
  ];

  // Build chip list: one chip per (animal, flag) so providers can see which
  // animal needs which prep. Skip animals with no flags set.
  const chips = [];
  for (const a of animals) {
    const n = privateNotes[a.id];
    if (!n) continue;
    for (const f of FLAG_DEFS) {
      if (n[f.key]) chips.push({ animalName: a.name, ...f });
    }
  }
  if (chips.length === 0) return null;

  return (
    <div
      className="flex flex-wrap gap-1.5 bg-rose-50 border border-rose-200 rounded p-1.5"
      data-testid="private-flag-chips"
    >
      <span className="text-[10px] uppercase tracking-wide font-medium text-rose-700 px-1 flex items-center">
        Your prep
      </span>
      {chips.map((c) => {
        const { Icon } = c;
        return (
          <span
            key={c.key}
            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-white border border-rose-300 text-rose-800"
            title={`${c.animalName}: ${c.label}`}
            data-testid={`flag-chip-${c.key}`}
          >
            <Icon className="h-3 w-3" />
            <span className="font-medium">{c.animalName}</span>
            <span className="text-rose-600">·</span>
            <span>{c.label}</span>
          </span>
        );
      })}
    </div>
  );
}
